import { createAsyncThunk, createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../index';
import type { BlogArticle, BlogArticleDraft, BlogCategory } from '../../nova/mock-data/types';
import {
  type ApiBlogArticle,
  type ApiBlogCategory,
  type BlogTenantStatus,
  articleDraftToApi,
  articleFromApi,
  blogApiErrorMessage,
  blogHttpStatus,
  blogFetchErrorMessage,
  blogRequest,
  blogRequestContext,
  categoryFromApi,
  categoryToApi,
  fetchBlogContent,
  fetchBlogTenantStatus,
  generateBlogQuiz,
  setBlogEnabled,
  uploadBlogMedia,
  type BlogQuizBrief,
  type BlogQuizDraft,
  type BlogWriteBrief,
  type BlogArticleJob,
  startBlogArticleJob,
  startBlogArticleGeneration,
  blogNeededCreditsOf,
  blogIsInsufficientCredits,
  listBlogArticleJobs,
  listBlogTenantJobs,
  type BlogAutogen,
  type BlogAutogenPatch,
  type BlogNotices,
  type BlogNoticesClear,
  fetchBlogAutogen,
  setBlogAutogen,
  resumeBlogAutogen,
  fetchBlogNotices,
  clearBlogNotices,
  autopilotCreditsShortfall,
} from './blog-api';

export type { BlogTenantStatus } from './blog-api';
export type { BlogQuizBrief, BlogQuizDraft } from './blog-api';
export type { BlogWriteBrief, BlogWritePart } from './blog-api';
export type { BlogArticleJob } from './blog-api';
export type {
  BlogAutogen,
  BlogAutogenReady,
  BlogAutogenSettings,
  BlogAutogenLastRun,
  BlogAutogenRunStatus,
  BlogAutogenPatch,
  BlogNotices,
  BlogNoticeArticle,
  BlogNoticesClear,
} from './blog-api';
export {
  BLOG_AUTOGEN_DEFAULT_INTERVAL_MINUTES,
  BLOG_AUTOGEN_UNAVAILABLE,
  BLOG_NOTICES_NONE,
  isBlogAutogenUnavailable,
  autopilotCreditsShortfall,
} from './blog-api';

/**
 * Blog authoring domain — articles + categories for the active brand's casino
 * blog (`docs/blog-authoring-plan.md` is the contract).
 *
 * The mock stage is over: every thunk calls the real spark-blog service via
 * `blog-api.ts` (routes tenant-scoped by UUID under `window.config.blogApiUrl`,
 * authenticated as the signed-in owner). The server owns
 * id/status/created/updated/published and all business rules — slug uniqueness,
 * publish/delete guards, and now tenant ownership — so the thunks carry no
 * local pre-checks: a business conflict (409), a missing tenant (404) or a
 * tenant that is not the caller's (403) all arrive as `{ error }` whose copy is
 * already a locale key, and flow through `rejectWithValue` into `saveError` —
 * or, for the read, into `error`. Components, selectors and lifecycle are
 * untouched from the mock stage.
 */

export type BlogState = {
  articles: BlogArticle[];
  categories: BlogCategory[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  /**
   * The newest generation job per article, as the SERVICE reports it — running,
   * done or failed.
   *
   * Keyed by article rather than held by whichever tab pressed Generate: that is
   * what lets a refreshed page, a second tab and another device all read the same
   * answer. Note an entry does NOT mean "writing" — read `status`, or use
   * `selectBlogArticleBusy`. A finished job stays here on purpose, because how
   * the last pass ended is exactly what the studio has to show once the spinner
   * stops.
   */
  jobs: Record<string, BlogArticleJob>;
  /**
   * Ids of the articles Lumio wrote in THIS session, newest first. The list
   * pulls them to the top so a finished draft lands where its generating row
   * was, instead of appearing at the end of the fetched order.
   */
  generated: string[];
  /** Any in-flight write (save / publish / category CRUD). */
  saving: boolean;
  saveError: string | null;
  /**
   * The `blogFetch` whose result the slice will accept. A brand switch resets
   * this slice (store/index.ts) while the previous brand's request is still
   * open, and the pages' guard effect immediately starts a second one — without
   * this stamp, whichever settles LAST wins, so brand A's articles can land
   * under brand B with `loaded: true` and no further refetch.
   */
  fetchRequestId: string | null;
  /**
   * Request ids of the writes this slice will still accept. Same reasoning as
   * `fetchRequestId`, for mutations: a save/publish in flight when `resetBlog`
   * fires (brand switch, logout, identity swap) would otherwise upsert the
   * PREVIOUS brand's article into the new brand's list — the brand switcher is
   * not disabled while a write is open.
   */
  activeWrites: string[];
  /**
   * Whether the brand's blog is switched on, or `null` until the tenant has
   * been read. Separate from `loaded`: the switch answers a different service
   * question than the article list, and it is the only one that can tell an
   * unprovisioned brand from an empty one — both list nothing, and only the
   * switch is what fixes the first.
   */
  tenantStatus: BlogTenantStatus | null;
  tenantStatusLoading: boolean;
  tenantStatusError: string | null;
  /** Race stamp for the tenant read, for the same reason as `fetchRequestId`. */
  tenantStatusRequestId: string | null;
  /**
   * The brand's Autopilot settings (`docs/auto-generation.md`), or `null`
   * until the endpoint has answered. `{ supported: false }` is an answer too:
   * the service build has no such route, and the panel hides.
   */
  autogen: BlogAutogen | null;
  autogenLoading: boolean;
  autogenError: string | null;
  /** Race stamp for the settings read, for the same reason as `fetchRequestId`. */
  autogenRequestId: string | null;
  /** The Autopilot notices (new articles, credits needed), or `null` until read. */
  notices: BlogNotices | null;
  noticesLoading: boolean;
  noticesError: string | null;
  /** Race stamp for the notices read. It is polled on tab return, so it needs one most. */
  noticesRequestId: string | null;
  /**
   * The credits settle in flight (`blogSettleAutopilotCredits`), or `null`.
   * One at a time: the layout's watcher and a top-up's own success callback
   * can both ask for it within the same tick.
   */
  autopilotSettleRequestId: string | null;
};

const initialState: BlogState = {
  articles: [],
  categories: [],
  loading: false,
  loaded: false,
  error: null,
  generated: [],
  jobs: {},
  saving: false,
  saveError: null,
  fetchRequestId: null,
  activeWrites: [],
  tenantStatus: null,
  tenantStatusLoading: false,
  tenantStatusError: null,
  tenantStatusRequestId: null,
  autogen: null,
  autogenLoading: false,
  autogenError: null,
  autogenRequestId: null,
  notices: null,
  noticesLoading: false,
  noticesError: null,
  noticesRequestId: null,
  autopilotSettleRequestId: null,
};

// ── Thunks ──────────────────────────────────────────────────────────────────

/**
 * GET .../articles + GET .../categories (full articles, no pagination).
 *
 * Rejects with the service's own copy, like the mutation thunks do — surfacing
 * the transport message instead would tell the owner "Request failed with
 * status code 403". `403 "You do not have access to this tenant"` (it is
 * someone else's) is the one business reason left for a read to fail: the
 * `404` the tenant-ownership change also introduced resolves as an empty blog
 * instead, because it is what a brand that has never turned the blog on looks
 * like — see {@link fetchBlogContent}.
 */
export const blogFetch = createAsyncThunk<
  { articles: BlogArticle[]; categories: BlogCategory[] },
  void,
  { state: RootState; rejectValue: { error: string } }
>('blog/fetch', async (_arg, { getState, rejectWithValue }) => {
  try {
    const { articles, categories } = await fetchBlogContent(blogRequestContext(getState()));
    return { articles: articles.map(articleFromApi), categories: categories.map(categoryFromApi) };
  } catch (err) {
    return rejectWithValue({ error: blogFetchErrorMessage(err) });
  }
});

/**
 * GET .../tenant/{id}: is this brand's blog switched on?
 *
 * A READ, so it is excluded from the shared write lifecycle at the bottom of
 * this file exactly like `blogFetch` is: it must not take the page's write lock
 * and its failure is not a `saveError`.
 */
export const blogFetchTenantStatus = createAsyncThunk<
  BlogTenantStatus,
  void,
  { state: RootState; rejectValue: { error: string } }
>('blog/fetchTenantStatus', async (_arg, { getState, rejectWithValue }) => {
  try {
    return await fetchBlogTenantStatus(blogRequestContext(getState()));
  } catch (err) {
    return rejectWithValue({ error: blogFetchErrorMessage(err) });
  }
});

/**
 * Turn the brand's blog on or off (owner-hub's control-plane switch), then read
 * the tenant back for the state that actually landed.
 *
 * The read-back is the point: the control plane can refuse the change on rules
 * this client cannot evaluate (a banned tenant, or the re-enable rate limit
 * that allows one off-to-on transition per `min-reenable-interval`), and
 * enabling a brand for the first time CREATES the tenant, which no local guess
 * could know. A refusal arrives as a rejection carrying the service's own copy;
 * the switch then simply keeps showing the state the tenant is really in.
 *
 * If only the read-back fails, the change still happened, so the requested
 * value is reported rather than thrown away. `banned: false` is safe to assume
 * there: a banned tenant cannot reach this line, since its enable is refused
 * and its disable leaves it banned-and-off, which is what the next load shows.
 * `languages` carries over from the last successful read for the same reason
 * the flags do not: the switch cannot change which languages a casino runs.
 */
export const blogSetEnabled = createAsyncThunk<
  BlogTenantStatus,
  boolean,
  { state: RootState; rejectValue: { error: string } }
>('blog/setEnabled', async (enabled, { getState, rejectWithValue }) => {
  try {
    // One context for both calls, deliberately: the read-back has to describe
    // the tenant the write targeted. Re-resolving it would follow a brand
    // switch that happened mid-flight and report the NEW brand's state as this
    // write's result (the reducer drops it either way, via `claimWrite`).
    const state = getState();
    const ctx = blogRequestContext(state);
    // Snapshot BEFORE the write, from the same state the context came from, so
    // the fallback below describes the tenant this write targeted.
    const knownLanguages = state.blog.tenantStatus?.languages ?? [];
    await setBlogEnabled(ctx, enabled);
    try {
      return await fetchBlogTenantStatus(ctx);
    } catch (err) {
      // A 403 is not a read that merely failed: it says this caller cannot see
      // the tenant AT ALL — it is unowned (admin-only) or someone else's. There
      // is nothing behind a "The blog is live" toast in that state, and the
      // owner is exactly as locked out as before they touched the switch, so
      // the refusal has to travel instead of being papered over (SB-239).
      if (blogHttpStatus(err) === 403) throw err;
      // The switch does not touch the casino's languages, so the last set we
      // read stays true. Reporting `[]` here would empty the article-language
      // picker down to its fallback pair on a toggle whose read-back failed.
      return { provisioned: true, enabled, banned: false, languages: knownLanguages };
    }
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/** POST .../articles (no id) / PUT .../articles/:id */
export const blogSaveArticle = createAsyncThunk<
  BlogArticle,
  BlogArticleDraft,
  { state: RootState; rejectValue: { error: string } }
>('blog/saveArticle', async (draft, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    const body = articleDraftToApi(draft);
    const saved = draft.id
      ? await blogRequest<ApiBlogArticle>(ctx, 'put', `/articles/${encodeURIComponent(draft.id)}`, body)
      : await blogRequest<ApiBlogArticle>(ctx, 'post', '/articles', body);
    return articleFromApi(saved);
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/** POST .../articles/:id/publish — returns the FULL updated article. */
export const blogPublishArticle = createAsyncThunk<
  BlogArticle,
  string,
  { state: RootState; rejectValue: { error: string } }
>('blog/publishArticle', async (id, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    return articleFromApi(await blogRequest<ApiBlogArticle>(ctx, 'post', `/articles/${encodeURIComponent(id)}/publish`));
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/** POST .../articles/:id/unpublish — returns the FULL updated article. */
export const blogUnpublishArticle = createAsyncThunk<
  BlogArticle,
  string,
  { state: RootState; rejectValue: { error: string } }
>('blog/unpublishArticle', async (id, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    return articleFromApi(
      await blogRequest<ApiBlogArticle>(ctx, 'post', `/articles/${encodeURIComponent(id)}/unpublish`),
    );
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/** DELETE .../articles/:id (drafts only; a published delete is a server 409). */
export const blogDeleteArticle = createAsyncThunk<
  { id: string },
  string,
  { state: RootState; rejectValue: { error: string } }
>('blog/deleteArticle', async (id, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    await blogRequest<void>(ctx, 'delete', `/articles/${encodeURIComponent(id)}`);
    return { id };
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/** POST .../categories (no id) / PUT .../categories/:id */
export const blogSaveCategory = createAsyncThunk<
  BlogCategory,
  Omit<BlogCategory, 'id'> & { id?: string },
  { state: RootState; rejectValue: { error: string } }
>('blog/saveCategory', async (draft, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    const body = categoryToApi(draft);
    const saved = draft.id
      ? await blogRequest<ApiBlogCategory>(ctx, 'put', `/categories/${encodeURIComponent(draft.id)}`, body)
      : await blogRequest<ApiBlogCategory>(ctx, 'post', '/categories', body);
    return categoryFromApi(saved);
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/** DELETE .../categories/:id (a server 409 when articles still reference it). */
export const blogDeleteCategory = createAsyncThunk<
  { id: string },
  string,
  { state: RootState; rejectValue: { error: string } }
>('blog/deleteCategory', async (id, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    await blogRequest<void>(ctx, 'delete', `/categories/${encodeURIComponent(id)}`);
    return { id };
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/**
 * POST .../media — uploads one image, resolves to its public CDN URL.
 *
 * Nothing lands in the slice: the URL belongs to the caller's draft, not to
 * the loaded articles. It still runs through a thunk so it shares the write
 * lifecycle below, which is what keeps Save and Publish disabled until the
 * image has a URL to save.
 */
export const blogUploadMedia = createAsyncThunk<
  string,
  File,
  { state: RootState; rejectValue: { error: string } }
>('blog/uploadMedia', async (file, { getState, rejectWithValue }) => {
  try {
    return await uploadBlogMedia(blogRequestContext(getState()), file);
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/**
 * POST .../articles/quiz — Lumio writes one quiz block for the body the editor
 * currently holds.
 *
 * Nothing lands in the slice, and nothing lands on the server either: the quiz
 * belongs to the body the author is editing, and it reaches the database when
 * they save that body like any other change. It still runs through a thunk so it
 * shares the request context (base URL, tenant, the owner's token) with every
 * other blog call rather than reaching for `window.config` from a component.
 *
 * It takes SECONDS, so it needs none of the job machinery a generation has: the
 * editor greys its own button and shows the wait on the block being written.
 */
const currentTenant = (state: RootState): string | null => {
  try {
    return blogRequestContext(state).tenant;
  } catch {
    return null;
  }
};

/**
 * Copy shown when a generation lands under a different brand than it started
 * under. English, like every other thunk refusal here; the caller runs it
 * through `t()`.
 */
const BRAND_CHANGED = 'You switched brand while that was being written, so it was not applied';

/**
 * Did this rejection mean the brand moved under the request?
 *
 * The difference matters to anything that RETRIES: a transient failure is worth
 * another tick, a brand switch makes the whole turn void and must end it. A
 * caller that cannot tell them apart either gives up too early or never gives
 * up at all.
 */
export const isBrandChanged = (err: unknown): boolean => blogSaveErrorMessage(err) === BRAND_CHANGED;

/**
 * The brand moved under a request that was already in flight.
 *
 * `resetBlog` fires on a brand switch, a logout and an impersonation identity
 * swap (see `store/index.ts`) precisely so one owner's blog content never
 * carries into the next. Every async write here is guarded against settling
 * afterwards — reads by `fetchRequestId`, mutations by `claimWrite` — and the
 * polls need it most of all: they run on a timer against generations that last
 * minutes, so one is in flight for most of a job's life. Without this, a tick
 * that answers just after the switch reinstalls the PREVIOUS brand's jobs into
 * the fresh state, where nothing ever removes them again.
 */
const staleBrand = (state: RootState, ctx: { tenant: string }): boolean => currentTenant(state) !== ctx.tenant;

/**
 * Re-read the articles once a generation has actually landed.
 *
 * The poll that learns a pass ended is the only thing that knows the article it
 * wrote into is now stale, so the refresh belongs HERE rather than on each
 * surface. Three surfaces used to decide it independently, each watching for a
 * running→done transition of its own — and a transition is exactly what a page
 * misses while it is unmounted, or when another surface resolves the ending
 * first. The result was a form still showing pre-generation text, with Save
 * enabled, over an article the service had already rewritten.
 *
 * Only `done` refreshes: a failed pass wrote nothing, so there is nothing to
 * re-read and the reason is already on the job.
 */
const refreshAfter = (
  job: BlogArticleJob | null | undefined,
  // Typed by what it is used for rather than as the store's whole dispatch: a
  // thunk's own dispatch is narrower than AppDispatch, and this only ever needs
  // to fire one thunk.
  dispatch: (thunk: ReturnType<typeof blogFetch>) => unknown,
): void => {
  if (job?.status === 'done') void dispatch(blogFetch());
};

/**
 * Shown when a job is `failed` but carries no reason. The service records the
 * upstream's own words, so this is a fallback rather than the usual copy — but a
 * failure with an empty reason must still read as a failure, not as silence.
 */
const GENERATION_FAILED = 'The article could not be written; try again';

/**
 * How often the studio asks the service what is being written.
 *
 * Shared by the list and the editor deliberately: they are asking the same
 * question about the same rows, and two different intervals would show the same
 * generation ending at two different moments. A couple of seconds is well under
 * what an owner reads as "it noticed", and every tick is one small query.
 */
export const JOB_POLL_MS = 3000;

/**
 * How long a surface will WAIT on one generation before giving up on it.
 *
 * Past the service's own thirty-minute deadline, so it never cuts a job short —
 * it exists for the case where the job stops being readable from here at all
 * (a logout, a brand switch), which no server-side deadline can end.
 */
export const JOB_WAIT_LIMIT_MS = 31 * 60 * 1000;

/**
 * Poll `thunk` on the shared cadence, but never in a backgrounded tab.
 *
 * A blog page left open in a tab nobody is looking at was asking the service for
 * job state every three seconds indefinitely — ~1,200 requests an hour for a
 * fact that changes at most twice. Same treatment the operations poller already
 * gives the same problem (`useOperationsLiveEvents`), and a return to the tab
 * refreshes immediately so nothing is stale for a tick.
 *
 * Returns the interval teardown, so a caller's effect can return it directly.
 */
export const pollWhileVisible = (run: () => void): (() => void) => {
  const tick = (): void => {
    if (document.visibilityState === 'visible') run();
  };

  const timer = window.setInterval(tick, JOB_POLL_MS);
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') run();
  };

  document.addEventListener('visibilitychange', onVisible);

  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
};

/* ── Generation jobs ──────────────────────────────────────────────────────── */

/**
 * Start a generation on an existing article and return as soon as the service
 * has claimed it.
 *
 * Nothing is awaited here beyond the claim: the writing outlives this call, this
 * page and this browser. A refusal with the service's own words is what a second
 * device gets when one is already running — the guard is a unique index over
 * there, not a check over here, because a check here only ever protects one tab.
 */
export const blogStartArticleJob = createAsyncThunk<
  BlogArticleJob,
  { articleId: string; brief: BlogWriteBrief },
  { state: RootState; rejectValue: { error: string; needed?: number; insufficient?: boolean } }
>('blog/startArticleJob', async ({ articleId, brief }, { getState, rejectWithValue }) => {
  try {
    return await startBlogArticleJob(blogRequestContext(getState()), articleId, brief);
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err), needed: blogNeededCreditsOf(err), insufficient: blogIsInsufficientCredits(err) || undefined });
  }
});

/**
 * Create the draft and start the whole-article pass filling it.
 *
 * The draft lands in the list immediately, with its job attached, and the
 * writing happens behind it. That is what makes the "writing…" row survive a
 * refresh: it is a real article now, not a placeholder this tab is holding.
 */
export const blogStartArticleGeneration = createAsyncThunk<
  { article: BlogArticle; job: BlogArticleJob },
  BlogWriteBrief,
  { state: RootState; rejectValue: { error: string; needed?: number; insufficient?: boolean } }
>('blog/startArticleGeneration', async (brief, { getState, rejectWithValue }) => {
  try {
    return await startBlogArticleGeneration(blogRequestContext(getState()), brief);
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err), needed: blogNeededCreditsOf(err), insufficient: blogIsInsufficientCredits(err) || undefined });
  }
});

/**
 * Ask the service where an article's generation got to.
 *
 * This is the call that makes the whole feature true: it is what a reopened
 * page, a second tab and another device each use to find out what is happening
 * to an article they did not start writing.
 *
 * It asks in two steps, and the second one is the point. A tick asks only for
 * what is RUNNING, because the service returns an article's whole job history
 * when asked without a status, and an article generated a dozen times would
 * send a dozen rows every few seconds. But "nothing is running" is two
 * different answers wearing the same face: the pass finished and the service
 * already wrote it into the article, or the pass FAILED and wrote nothing, with
 * the reason on the job row. Treating both as "it landed" is how a failed
 * generation stops its spinner, changes nothing, and says nothing at all.
 *
 * So when a tick comes back empty for an article that was running a moment ago,
 * it asks once more without a status and reports the outcome. That is one extra
 * request per generation, not per tick — and for an article that was not
 * running, none: there is no outcome to report to a page that was not watching.
 */
export const blogPollArticleJobs = createAsyncThunk<
  { articleId: string; job: BlogArticleJob | null },
  string,
  { state: RootState; rejectValue: { error: string } }
>('blog/pollArticleJobs', async (articleId, { getState, dispatch, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    const running = await listBlogArticleJobs(ctx, articleId, 'running');

    if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });

    if (running[0]) return { articleId, job: running[0] };

    const known = getState().blog.jobs[articleId];

    if (known?.status === 'running') {
      // It was running when this tick started and is not now: read how it ended.
      // Newest first, so the head is the pass that just finished.
      const history = await listBlogArticleJobs(ctx, articleId);

      if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });

      // A pass that LANDED changed the article, so the articles this studio is
      // holding are now stale — everywhere, not just on whichever page happens
      // to be mounted. See `refreshAfter`.
      refreshAfter(history[0], dispatch);

      return { articleId, job: history[0] ?? null };
    }

    /*
     * Nothing running, and what is recorded has already ended. Report it back
     * UNCHANGED rather than as nothing.
     *
     * Returning null here is what the reducer reads as "forget this article",
     * and it is the only case where that delete does anything — so a `failed`
     * job was erased by the very next tick, three seconds after the studio put
     * its reason on screen. The success path was no better: the outcome could
     * vanish between the tick that recorded `done` and the effect watching for
     * it, so the re-read never ran and the stale form was saved back over the
     * article the service had just written.
     */
    return { articleId, job: known ?? null };
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/**
 * Ask the service what is being written across the whole brand.
 *
 * This is what the blog LIST polls, and it exists because the per-article call
 * cannot answer the list's question: a page that has just opened knows no
 * article ids worth asking about, and the generations it most needs to show are
 * exactly the ones it did not start.
 *
 * It resolves endings the same way the per-article poll does, and for the same
 * reason: an article that was running and is no longer in the running set has
 * ENDED, and "ended" is either a result already written into the article or a
 * failure that wrote nothing and left its reason on the job row. The tenant
 * query is asked for `running` only — unfiltered it returns every generation the
 * brand has ever run — so the outcome has to be read per article. One extra
 * request per generation that ends, none on a quiet tick.
 */
export const blogPollTenantJobs = createAsyncThunk<
  { running: BlogArticleJob[]; resolved: BlogArticleJob[] },
  void,
  { state: RootState; rejectValue: { error: string } }
>('blog/pollTenantJobs', async (_, { getState, dispatch, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    const running = await listBlogTenantJobs(ctx, 'running');

    if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });

    const stillRunning = new Set(running.map((job) => job.articleId));
    const ended = Object.values(getState().blog.jobs)
      .filter((job) => job.status === 'running' && !stillRunning.has(job.articleId))
      .map((job) => job.articleId);

    // Per ARTICLE, not tenant-wide: this asks for history, and the tenant's
    // whole history is every generation the brand has ever run. One small
    // request per generation that just ended.
    const resolved = await Promise.all(
      ended.map(async (articleId) => {
        const history = await listBlogArticleJobs(ctx, articleId).catch(() => []);

        return history[0] ?? null;
      }),
    );

    if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });

    const landed = resolved.filter((job): job is BlogArticleJob => job !== null);

    landed.forEach((job) => refreshAfter(job, dispatch));

    return { running, resolved: landed };
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

export const blogGenerateQuiz = createAsyncThunk<
  BlogQuizDraft,
  BlogQuizBrief,
  { state: RootState; rejectValue: { error: string; needed?: number; insufficient?: boolean } }
>('blog/generateQuiz', async (brief, { getState, signal, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    const quiz = await generateBlogQuiz(ctx, brief, signal);

    // Seconds rather than minutes, but the same window and the same outcome:
    // one brand's quiz pasted into another brand's article.
    if (currentTenant(getState()) !== ctx.tenant) return rejectWithValue({ error: BRAND_CHANGED });

    return quiz;
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err), needed: blogNeededCreditsOf(err), insufficient: blogIsInsufficientCredits(err) || undefined });
  }
});

/**
 * One article's new place in its category's reading order. `pillar` travels with
 * it because the two are the same fact: the public sort is `pillar, then
 * sequence`, so an article dragged across the "Start here" seam has changed both.
 */
export type BlogReorderEntry = { id: string; sequence: number; pillar: boolean };

/**
 * Writes a new reading order across a category — one `PUT /articles/:id` per
 * article that actually moved.
 *
 * The service has no bulk-reorder route, and its update is a FULL replace of the
 * draft, so each entry is sent as its stored article with only its PLACE
 * (`sequence` + `pillar`) swapped. Two consequences the caller has to live with: every moved article's
 * `updatedAt` bumps even though its content did not change, and an article a
 * colleague edited since this tab last fetched is written back from the stale
 * local copy. Only the articles the author actually moved are sent, which keeps
 * both effects to the smallest possible set.
 *
 * Fan-out failures are partial by nature, so this thunk does not reject on one:
 * it fulfils with the articles that DID land (the reducer upserts exactly those)
 * plus the first error message, and lets the caller report it. Rejecting would
 * throw away the successful writes' fresh copies while the server keeps them.
 */
export const blogReorderArticles = createAsyncThunk<
  { saved: BlogArticle[]; error?: string },
  BlogReorderEntry[],
  { state: RootState }
>('blog/reorderArticles', async (entries, { getState }) => {
  const state = getState();
  const ctx = blogRequestContext(state);

  const writes = entries
    .map((entry) => {
      const current = state.blog.articles.find((a) => a.id === entry.id);
      // Skip an id the slice no longer holds (deleted in another tab) and any
      // entry that would rewrite the place it already holds.
      if (!current || (current.sequence === entry.sequence && current.pillar === entry.pillar)) return null;
      const { status, updatedAt, publishedAt, ...draft } = current;
      void status;
      void updatedAt;
      void publishedAt;
      return {
        id: entry.id,
        body: articleDraftToApi({ ...draft, sequence: entry.sequence, pillar: entry.pillar }),
      };
    })
    .filter((write): write is { id: string; body: ReturnType<typeof articleDraftToApi> } => write !== null);

  const results = await Promise.allSettled(
    writes.map((write) =>
      blogRequest<ApiBlogArticle>(ctx, 'put', `/articles/${encodeURIComponent(write.id)}`, write.body),
    ),
  );

  const saved = results
    .filter((result): result is PromiseFulfilledResult<ApiBlogArticle> => result.status === 'fulfilled')
    .map((result) => articleFromApi(result.value));
  const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');

  return { saved, error: failure ? blogApiErrorMessage(failure.reason) : undefined };
});

/* ── Autopilot ────────────────────────────────────────────────────────────── */

/**
 * GET .../auto-generation: the brand's Autopilot settings.
 *
 * A READ, kept out of the shared write lifecycle below like `blogFetch` and
 * the tenant read: it must not take the page's write lock, and its failure is
 * its own error, not a `saveError`. It carries the same stale-brand stamp as
 * the other reads (`autogenRequestId`), and the thunk itself also checks the
 * brand after the answer: a settings document for brand A must never render
 * as brand B's switch, and this one is re-read on tab return, so one is in
 * flight more often than the others.
 *
 * A 404 resolves rather than rejects (see `fetchBlogAutogen`): an older
 * service build is "no panel", not "Retry".
 */
export const blogFetchAutogen = createAsyncThunk<
  BlogAutogen,
  void,
  { state: RootState; rejectValue: { error: string } }
>('blog/fetchAutogen', async (_arg, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    const autogen = await fetchBlogAutogen(ctx);
    if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });
    return autogen;
  } catch (err) {
    return rejectWithValue({ error: blogFetchErrorMessage(err) });
  }
});

/**
 * PUT .../auto-generation: turn Autopilot on (with the byline it will sign
 * with) or off. A WRITE: it joins the page lock through the shared matchers
 * and releases it in its own fulfilled case. The service answers the settings
 * that actually landed, and those are what the slice installs; a refused
 * enable (no owner, banned, writer off) rejects with the service's own copy.
 */
export const blogSetAutogen = createAsyncThunk<
  BlogAutogen,
  BlogAutogenPatch,
  { state: RootState; rejectValue: { error: string } }
>('blog/setAutogen', async (patch, { getState, rejectWithValue }) => {
  try {
    return await setBlogAutogen(blogRequestContext(getState()), patch);
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/**
 * POST .../auto-generation/resume: after a top-up, ask for the missed article
 * soon rather than at the next slot. A write (it reschedules a run), so it
 * shares the lock and releases it like `blogSetAutogen`.
 */
export const blogResumeAutogen = createAsyncThunk<
  BlogAutogen,
  void,
  { state: RootState; rejectValue: { error: string } }
>('blog/resumeAutogen', async (_arg, { getState, rejectWithValue }) => {
  try {
    return await resumeBlogAutogen(blogRequestContext(getState()));
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/**
 * GET .../notices: the "N new articles" and "needs credits" notices the Home
 * Spotlight shows. A read, polled on mount, tab return and brand switch (no
 * interval), so it is excluded from the write lock and stamped against the
 * brand exactly as `blogFetchAutogen` is.
 */
export const blogFetchNotices = createAsyncThunk<
  BlogNotices,
  void,
  { state: RootState; rejectValue: { error: string } }
>('blog/fetchNotices', async (_arg, { getState, rejectWithValue }) => {
  try {
    const ctx = blogRequestContext(getState());
    const notices = await fetchBlogNotices(ctx);
    if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });
    return notices;
  } catch (err) {
    return rejectWithValue({ error: blogFetchErrorMessage(err) });
  }
});

/**
 * POST .../notices/clear: close the article list and/or the credits notice.
 * A write; installs the notices as they stand afterwards.
 */
export const blogClearNotices = createAsyncThunk<
  BlogNotices,
  BlogNoticesClear,
  { state: RootState; rejectValue: { error: string } }
>('blog/clearNotices', async (which, { getState, rejectWithValue }) => {
  try {
    return await clearBlogNotices(blogRequestContext(getState()), which);
  } catch (err) {
    return rejectWithValue({ error: blogApiErrorMessage(err) });
  }
});

/**
 * What `blogSettleAutopilotCredits` found, and what it did about it.
 *
 * - `settled`: the balance covers the next article, so the notice was closed
 *   and the missed article asked for (`autogen` is the settings `resume`
 *   answered, or null when the resume failed: the notice is closed anyway, and
 *   the article waits for its slot);
 * - `closed`: the notice was already closed when read again (a run succeeded,
 *   or another tab settled it), so nothing was written;
 * - `short`: the balance does not cover it, so it stays open.
 */
export type BlogAutopilotSettle = {
  outcome: 'settled' | 'closed' | 'short';
  /** The notices as they stand now. */
  notices: BlogNotices;
  /**
   * 'settled': the settings `resume` answered, or null when it failed.
   * 'closed' and 'short': the settings read for the price, or null when that
   * read failed; a read, so the reducer only carries its price over.
   */
  autogen: BlogAutogen | null;
};

/**
 * Close an Autopilot credits notice the owner's balance now covers, and ask
 * for the missed article (`docs/auto-generation.md` in spark-blog, "Owner
 * Studio behaviour").
 *
 * The notice is the service's record of a refused run, and it stays open until
 * a run succeeds, it is cleared, or Autopilot is turned off: the service never
 * hears of a top-up. So the studio closes it whenever the balance covers it,
 * whichever way the credits arrived. The notice is read again first, and the
 * balance is the one the caller has just re-read into the wallet slice
 * (`settleAutopilotTopup`): Autopilot spends on the service, so a balance this
 * tab read earlier can be higher than the real one, and closing a notice the
 * wallet cannot cover would only buy a second refusal, and a second email.
 *
 * The price is the settings', read here every time: a notice keeps the price
 * of its refusal, and the settings a page holds may be older than a change of
 * price.
 *
 * Not a page write: it runs in the background whenever the balance is seen to
 * cover an open notice, so it takes no write lock (the list and the editor
 * would grey out for an action the owner never took) and a failure is no save
 * error. It carries the stale-brand stamp the reads carry, and runs one at a
 * time.
 */
export const blogSettleAutopilotCredits = createAsyncThunk<
  BlogAutopilotSettle,
  /**
   * `balance`: the balance to decide on, from a caller that has just seen it
   * read (a top-up's success). Absent: the wallet slice's, unless its last
   * read failed.
   */
  { balance?: number | null } | void,
  { state: RootState; rejectValue: { error: string } }
>(
  'blog/settleAutopilotCredits',
  async (arg, { getState, rejectWithValue }) => {
    try {
      const ctx = blogRequestContext(getState());
      // What the next article costs now: the notice keeps the price of its
      // refusal, and a price raised since would refuse the resumed run again.
      // Read before the notice, so nothing is awaited between reading the
      // notice and deciding on it: a "Clear" pressed meanwhile would otherwise
      // be undone by this answer. The settings only refine the price, so a
      // read that fails falls back to the price a page holds rather than
      // dropping the settle.
      const held = getState().blog.autogen;
      const settings = await fetchBlogAutogen(ctx).catch(() => null);
      const notices = await fetchBlogNotices(ctx);
      if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });
      if (notices.credits === null) return { outcome: 'closed', notices, autogen: settings };

      const priced = settings ?? held;
      const shortfall = autopilotCreditsShortfall({
        needed: notices.credits.needed,
        balance: arg && arg.balance !== undefined ? arg.balance : selectBlogCreditsBalance(getState()),
        price: priced !== null && priced.supported ? priced.pricePerArticle : null,
      });
      if (shortfall !== null) return { outcome: 'short', notices, autogen: settings };

      // Neither write stops the other: the credits are there, so the missed
      // article is asked for even when the notice could not be closed (a run
      // that publishes closes it on the service).
      const cleared = await clearBlogNotices(ctx, { credits: true }).catch(() => null);
      const autogen = await resumeBlogAutogen(ctx).catch(() => null);
      if (cleared === null && autogen === null) {
        return rejectWithValue({ error: 'The credits notice could not be settled' });
      }
      if (staleBrand(getState(), ctx)) return rejectWithValue({ error: BRAND_CHANGED });
      return { outcome: 'settled', notices: cleared ?? notices, autogen };
    } catch (err) {
      return rejectWithValue({ error: blogFetchErrorMessage(err) });
    }
  },
  { condition: (_arg, { getState }) => getState().blog.autopilotSettleRequestId === null },
);

// ── Slice ───────────────────────────────────────────────────────────────────

/**
 * True when the slice still awaits this write; also retires the id so a handler
 * runs at most once. A write dropped here was superseded by `resetBlog`.
 */
const claimWrite = (state: BlogState, requestId: string): boolean => {
  const index = state.activeWrites.indexOf(requestId);
  if (index === -1) return false;
  state.activeWrites.splice(index, 1);
  return true;
};

const upsert = (articles: BlogArticle[], article: BlogArticle): BlogArticle[] => {
  const index = articles.findIndex((a) => a.id === article.id);
  if (index === -1) return [...articles, article];
  const next = articles.slice();
  next[index] = article;
  return next;
};

/**
 * Retire a settings or notices read that is still out: the write that just
 * answered is at least as fresh, and a read that left before it would land on
 * top of it (a refresh that left before a top-up's clear reopens the notice
 * the clear closed). A first read retired here leaves nothing behind, so the
 * page's guard simply asks again.
 */
const dropAutogenRead = (state: BlogState): void => {
  state.autogenRequestId = null;
  state.autogenLoading = false;
};

const dropNoticesRead = (state: BlogState): void => {
  state.noticesRequestId = null;
  state.noticesLoading = false;
};

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    resetBlog: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(blogFetch.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.fetchRequestId = action.meta.requestId;
      })
      .addCase(blogFetch.fulfilled, (state, action) => {
        // Ignore a response the slice no longer awaits: `resetBlog` (logout,
        // brand switch, identity swap) clears the stamp, so a request issued
        // for the PREVIOUS tenant cannot install its articles here.
        if (state.fetchRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.loaded = true;
        state.articles = action.payload.articles;
        state.categories = action.payload.categories;
        state.fetchRequestId = null;
      })
      .addCase(blogFetch.rejected, (state, action) => {
        if (state.fetchRequestId !== action.meta.requestId) return;
        state.loading = false;
        // `payload` is the thunk's own message (service copy or context error);
        // `error.message` only survives for a rejection it never produced — an
        // abort, or a throw outside the try.
        state.error = action.payload?.error ?? action.error.message ?? 'Failed to load the blog';
        state.fetchRequestId = null;
      })
      // The blog switch. Its read carries the same stale-brand stamp as
      // `blogFetch`: a brand switch resets this slice while the previous
      // brand's tenant read is open, and the page's guard effect starts a
      // second one immediately, so without the stamp brand A's "live" can land
      // under brand B.
      .addCase(blogFetchTenantStatus.pending, (state, action) => {
        state.tenantStatusLoading = true;
        state.tenantStatusError = null;
        state.tenantStatusRequestId = action.meta.requestId;
      })
      .addCase(blogFetchTenantStatus.fulfilled, (state, action) => {
        if (state.tenantStatusRequestId !== action.meta.requestId) return;
        state.tenantStatusLoading = false;
        state.tenantStatus = action.payload;
        state.tenantStatusRequestId = null;
      })
      .addCase(blogFetchTenantStatus.rejected, (state, action) => {
        if (state.tenantStatusRequestId !== action.meta.requestId) return;
        state.tenantStatusLoading = false;
        state.tenantStatusError = action.payload?.error ?? action.error.message ?? 'Failed to load the blog';
        state.tenantStatusRequestId = null;
      })
      // The switch WRITE takes the page's write lock through the shared
      // matchers, so it owes them an explicit release here (as `blogUploadMedia`
      // does) or `saving` sticks and greys out publish and the category actions.
      // A stale-brand write is dropped by `claimWrite`, so the previous brand's
      // switch cannot report its state under the new brand.
      .addCase(blogSetEnabled.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.tenantStatus = action.payload;
        // The tenant just answered, so any earlier read failure is stale.
        state.tenantStatusError = null;
      })
      .addCase(blogSaveArticle.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.articles = upsert(state.articles, action.payload);
      })
      // Publish/unpublish return the FULL updated article (status, publishedAt,
      // updatedAt all server-resolved) — upsert it wholesale.
      .addCase(blogPublishArticle.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.articles = upsert(state.articles, action.payload);
      })
      .addCase(blogUnpublishArticle.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.articles = upsert(state.articles, action.payload);
      })
      // A job the SERVICE has acknowledged. Recorded against the article, not
      // against this tab, so every surface reads the same answer.
      .addCase(blogStartArticleJob.fulfilled, (state, action) => {
        // Claiming is a write, so it took the page lock on the way in and owes
        // an explicit release here — see `blogSetEnabled.fulfilled`. Without it
        // `saving` sticks after the FIRST Generate and every control on the
        // editor stays inert for the rest of the session, with the generation
        // itself running perfectly well behind it.
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.jobs[action.payload.articleId] = action.payload;
      })
      .addCase(blogStartArticleGeneration.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.articles = upsert(state.articles, action.payload.article);
        state.jobs[action.payload.job.articleId] = action.payload.job;
        // The placeholder draft is what the list shows while it fills, so it
        // goes to the top with the other freshly-generated ones.
        state.generated = [action.payload.article.id, ...state.generated.filter((id) => id !== action.payload.article.id)];
      })
      // The poll is the source of truth in BOTH directions: a job appears here
      // for a page that never started one (another device did), and disappears
      // when it lands — the empty tick is how the studio learns to re-read the
      // article the service has already written into.
      .addCase(blogPollArticleJobs.fulfilled, (state, action) => {
        if (action.payload.job) {
          state.jobs[action.payload.articleId] = action.payload.job;

          return;
        }

        // Only a job still believed to be RUNNING may be cleared by an empty
        // answer. A finished one is the outcome the studio is showing — the
        // reason a failure gives, and the signal a success re-reads on — so
        // deleting it here is how both used to disappear.
        if (state.jobs[action.payload.articleId]?.status === 'running') {
          delete state.jobs[action.payload.articleId];
        }
      })
      /*
       * The brand-wide tick. `running` is the whole truth about what is being
       * written right now, so an article whose job is absent from it has ended —
       * but ended is not the same as gone, and the resolved outcome is what says
       * which. Both are written the same way: the newest job wins, whatever its
       * status.
       */
      .addCase(blogPollTenantJobs.fulfilled, (state, action) => {
        for (const job of [...action.payload.running, ...action.payload.resolved]) {
          state.jobs[job.articleId] = job;
        }
      })
      .addCase(blogDeleteArticle.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.articles = state.articles.filter((a) => a.id !== action.payload.id);
        // The service cascades the job rows; keeping them here would leave a
        // deleted article looking like it was still being written.
        delete state.jobs[action.payload.id];
      })
      // A reorder fan-out always fulfils: it upserts whichever articles landed and
      // reports the rest through the payload's `error` (see the thunk).
      .addCase(blogReorderArticles.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.articles = action.payload.saved.reduce(upsert, state.articles);
        if (action.payload.error) state.saveError = action.payload.error;
      })
      .addCase(blogSaveCategory.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        const index = state.categories.findIndex((c) => c.id === action.payload.id);
        if (index === -1) state.categories.push(action.payload);
        else state.categories[index] = action.payload;
      })
      .addCase(blogDeleteCategory.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.categories = state.categories.filter((c) => c.id !== action.payload.id);
      })
      // An upload changes no slice data, but it still has to release the write
      // lock the shared pending matcher below took.
      .addCase(blogUploadMedia.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
      })
      // Autopilot settings. The READ carries the stale-brand stamp the other
      // reads do; the WRITES go through the shared matchers and release the
      // lock here, as `blogSetEnabled` does. Every write answers the whole
      // settings document, so each installs it wholesale and clears any
      // earlier read failure: the endpoint just answered.
      .addCase(blogFetchAutogen.pending, (state, action) => {
        state.autogenLoading = true;
        state.autogenError = null;
        state.autogenRequestId = action.meta.requestId;
      })
      .addCase(blogFetchAutogen.fulfilled, (state, action) => {
        if (state.autogenRequestId !== action.meta.requestId) return;
        state.autogenLoading = false;
        state.autogen = action.payload;
        state.autogenRequestId = null;
      })
      .addCase(blogFetchAutogen.rejected, (state, action) => {
        if (state.autogenRequestId !== action.meta.requestId) return;
        state.autogenLoading = false;
        state.autogenError = action.payload?.error ?? action.error.message ?? 'Failed to load the blog';
        state.autogenRequestId = null;
      })
      .addCase(blogSetAutogen.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.autogen = action.payload;
        state.autogenError = null;
        dropAutogenRead(state);
      })
      .addCase(blogResumeAutogen.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.autogen = action.payload;
        state.autogenError = null;
        dropAutogenRead(state);
      })
      // Autopilot notices: same shape as the settings, read and write.
      .addCase(blogFetchNotices.pending, (state, action) => {
        state.noticesLoading = true;
        state.noticesError = null;
        state.noticesRequestId = action.meta.requestId;
      })
      .addCase(blogFetchNotices.fulfilled, (state, action) => {
        if (state.noticesRequestId !== action.meta.requestId) return;
        state.noticesLoading = false;
        state.notices = action.payload;
        state.noticesRequestId = null;
      })
      .addCase(blogFetchNotices.rejected, (state, action) => {
        if (state.noticesRequestId !== action.meta.requestId) return;
        state.noticesLoading = false;
        state.noticesError = action.payload?.error ?? action.error.message ?? 'Failed to load the blog';
        state.noticesRequestId = null;
      })
      .addCase(blogClearNotices.fulfilled, (state, action) => {
        if (!claimWrite(state, action.meta.requestId)) return;
        state.saving = false;
        state.notices = action.payload;
        state.noticesError = null;
        dropNoticesRead(state);
        // Clearing the credits notice closes it on the settings document too,
        // so the panel stops saying credits are needed without a second read,
        // and a settings read that left before that clear may not reopen it.
        // A clear of the article list alone changes nothing a settings read
        // carries, so it leaves such a read alone: the blog page's refresh on
        // open races "See articles" exactly so.
        if (action.payload.credits === null) {
          const autogen = state.autogen;
          if (autogen !== null && autogen.supported) autogen.creditsNeeded = null;
          if (action.meta.arg?.credits === true) dropAutogenRead(state);
        }
      })
      // The credits settle: not a page write (see the thunk), so it is kept out
      // of the shared matchers below and owns its whole lifecycle here. Only a
      // settle that WROTE installs its answers whole and retires the reads
      // still out; one that wrote nothing carries over only the credits notice
      // and the price (see below).
      .addCase(blogSettleAutopilotCredits.pending, (state, action) => {
        state.autopilotSettleRequestId = action.meta.requestId;
      })
      .addCase(blogSettleAutopilotCredits.fulfilled, (state, action) => {
        if (state.autopilotSettleRequestId !== action.meta.requestId) return;
        state.autopilotSettleRequestId = null;
        const { outcome, notices, autogen } = action.payload;
        const held = state.autogen;
        if (outcome === 'settled') {
          // Its own writes answered last: install them whole.
          state.notices = notices;
          state.noticesError = null;
          if (autogen !== null) {
            state.autogen = autogen;
            state.autogenError = null;
          } else if (held !== null && held.supported) {
            held.creditsNeeded = notices.credits?.needed ?? null;
          }
          dropNoticesRead(state);
          dropAutogenRead(state);
          return;
        }
        // Wrote nothing: its answers are reads, taken before any write that
        // answered while it ran (the Autopilot switch, which the settle does not
        // lock, or a Clear). Carry over only what it read them for, whether the
        // notice is open and what the next article costs, onto what the slice
        // holds; install a document whole only where none is held.
        state.notices = state.notices === null ? notices : { ...state.notices, credits: notices.credits };
        state.noticesError = null;
        if (held !== null && held.supported) {
          held.creditsNeeded = notices.credits?.needed ?? null;
          if (autogen !== null && autogen.supported) held.pricePerArticle = autogen.pricePerArticle;
        } else if (autogen !== null) {
          state.autogen = autogen;
          state.autogenError = null;
        }
      })
      .addCase(blogSettleAutopilotCredits.rejected, (state, action) => {
        if (state.autopilotSettleRequestId !== action.meta.requestId) return;
        state.autopilotSettleRequestId = null;
      })
      // Shared write lifecycle: every mutation thunk flips the same saving flag.
      // Five thunks stay out of it. `blogFetch` and `blogFetchTenantStatus` are
      // reads, as are the two job polls — and those run on a TIMER, so taking
      // the lock there would leave the page inert for as long as it was open.
      // `blogGenerateQuiz` writes NOTHING: no article changes, so there is no
      // write to lock and nothing a brand switch could misfile. The editor greys
      // its OWN controls while it runs, which is a different thing from the
      // page's write lock.
      //
      // Note what this matcher means for anything added later: a new thunk joins
      // the lock by existing, and only its own fulfilled case gives it back.
      .addMatcher(
        (action): action is PayloadAction & { meta: { requestId: string } } =>
          action.type.startsWith('blog/') &&
          action.type.endsWith('/pending') &&
          action.type !== blogFetch.pending.type &&
          action.type !== blogFetchTenantStatus.pending.type &&
          action.type !== blogGenerateQuiz.pending.type &&
          // The job polls are READS, and they run on a timer. Taking the page
          // write lock here would set `saving` on every tick and leave the
          // editor and the list inert for as long as either was open — the
          // exclusions above are the same reason, for the same kind of thunk.
          action.type !== blogPollArticleJobs.pending.type &&
          action.type !== blogPollTenantJobs.pending.type &&
          // The Autopilot settings and notices are READS too, and the notices
          // are re-read on every tab return: same exclusion, same reason.
          action.type !== blogFetchAutogen.pending.type &&
          action.type !== blogFetchNotices.pending.type &&
          // A write, but one the owner never asked for: it runs in the
          // background when the balance covers an open credits notice.
          action.type !== blogSettleAutopilotCredits.pending.type,
        (state, action) => {
          state.saving = true;
          state.saveError = null;
          state.activeWrites.push(action.meta.requestId);
        },
      )
      .addMatcher(
        (action): action is PayloadAction<{ error: string } | undefined> & { meta: { requestId: string } } =>
          action.type.startsWith('blog/') &&
          action.type.endsWith('/rejected') &&
          action.type !== blogFetch.rejected.type &&
          action.type !== blogFetchTenantStatus.rejected.type &&
          // Neither took the lock (see the pending matcher), and their refusals
          // are shown where the generation was happening rather than in the
          // page's banner.
          action.type !== blogGenerateQuiz.rejected.type &&
          // Never took the lock, and a poll that fails is not something to put
          // in the page's save banner: the next tick is a couple of seconds
          // away, and the generation is unaffected either way.
          action.type !== blogPollArticleJobs.rejected.type &&
          action.type !== blogPollTenantJobs.rejected.type &&
          // Their failures are filed as their own errors above, never in the
          // page's save banner.
          action.type !== blogFetchAutogen.rejected.type &&
          action.type !== blogFetchNotices.rejected.type &&
          action.type !== blogSettleAutopilotCredits.rejected.type,
        (state, action) => {
          if (!claimWrite(state, action.meta.requestId)) return;
          state.saving = false;
          state.saveError = action.payload?.error ?? 'The change could not be saved';
        },
      );
  },
});

export const { resetBlog } = blogSlice.actions;

/**
 * Message for a rejected mutation thunk's `.unwrap()` error. Thunks reject with
 * `{ error: <english copy> }`; callers route the result through `t()`.
 */
/**
 * The 402 refusal off a rejected generation thunk's `.unwrap()` error.
 * `blogInsufficientCredits` says whether the refusal happened at all (true
 * even when the response carried no readable shortfall); `blogNeededCredits`
 * is the shortfall to prime the Buy-credits sheet with, when known. The caller
 * that sees the refusal opens the sheet instead of toasting a dead end,
 * exactly as the creatives pages treat their 402s.
 */
export const blogInsufficientCredits = (err: unknown): boolean =>
  (typeof err === 'object' &&
    err !== null &&
    'insufficient' in err &&
    (err as { insufficient?: unknown }).insufficient === true) ||
  blogNeededCredits(err) !== undefined;

export const blogNeededCredits = (err: unknown): number | undefined =>
  typeof err === 'object' && err !== null && 'needed' in err && typeof (err as { needed: unknown }).needed === 'number'
    ? (err as { needed: number }).needed
    : undefined;

export const blogSaveErrorMessage = (err: unknown): string =>
  typeof err === 'object' && err !== null && 'error' in err && typeof (err as { error: unknown }).error === 'string'
    ? (err as { error: string }).error
    : 'The change could not be saved';

// ── Selectors ───────────────────────────────────────────────────────────────

export const selectBlog = (state: RootState): BlogState => state.blog;
export const selectBlogArticles = (state: RootState): BlogArticle[] => state.blog.articles;
export const selectBlogCategories = (state: RootState): BlogCategory[] => state.blog.categories;
export const selectBlogLoaded = (state: RootState): boolean => state.blog.loaded;
export const selectBlogLoading = (state: RootState): boolean => state.blog.loading;
export const selectBlogSaving = (state: RootState): boolean => state.blog.saving;

export const selectBlogTenantStatus = (state: RootState): BlogTenantStatus | null => state.blog.tenantStatus;
export const selectBlogTenantStatusLoading = (state: RootState): boolean => state.blog.tenantStatusLoading;
export const selectBlogTenantStatusError = (state: RootState): string | null => state.blog.tenantStatusError;

/** The Autopilot settings, `{ supported: false }` on an older build, or null until read. */
export const selectBlogAutogen = (state: RootState): BlogAutogen | null => state.blog.autogen;
export const selectBlogAutogenLoading = (state: RootState): boolean => state.blog.autogenLoading;
export const selectBlogAutogenError = (state: RootState): string | null => state.blog.autogenError;

/** The Autopilot notices (new articles, credits needed), or null until read. */
export const selectBlogNotices = (state: RootState): BlogNotices | null => state.blog.notices;
export const selectBlogNoticesLoading = (state: RootState): boolean => state.blog.noticesLoading;
export const selectBlogNoticesError = (state: RootState): string | null => state.blog.noticesError;

/**
 * The open credits notice's price, from whichever read holds it: the blog
 * page's settings or the Spotlight's notices. Null when neither has one open
 * (or neither has been read).
 */
export const selectBlogCreditsNoticeNeeded = (state: RootState): number | null => {
  const autogen = state.blog.autogen;
  const fromSettings = autogen !== null && autogen.supported ? autogen.creditsNeeded : null;
  return fromSettings ?? state.blog.notices?.credits?.needed ?? null;
};

/**
 * The owner's AI-credits balance as the studio last read it, or null when it
 * is not known: the wallet list never loaded, or its last read failed (a
 * failed read keeps the previous balance in the wallet slice, which may be
 * stale-high, since Autopilot spends on the service). It stays unknown until
 * a read succeeds, not merely starts, so the surfaces do not flicker while a
 * failing wallet is read again. The Autopilot credits rule
 * (`autopilotCreditsShortfall`) then takes the service at its word.
 */
export const selectBlogCreditsBalance = (state: RootState): number | null => {
  const wallet = state.ownerWallet;
  if (!wallet || wallet.creditsReadFailed) return null;
  return wallet.credits ?? null;
};

/** What one Autopilot article costs as the settings priced it, or null when unknown. */
export const selectBlogAutopilotPrice = (state: RootState): number | null => {
  const autogen = state.blog.autogen;
  return autogen !== null && autogen.supported ? autogen.pricePerArticle : null;
};

/** The newest generation job for an article, as the SERVICE reports it — present
 *  whether this tab started it, another tab did, or another device did. It may be
 *  running, done or failed; check `status` before reading it as "writing". */
export const selectBlogArticleJob = (articleId: string | undefined) => (state: RootState): BlogArticleJob | null =>
  articleId ? (state.blog.jobs[articleId] ?? null) : null;

/** Whether anything is being written for this article. The one-at-a-time guard's
 *  reflection in the UI: it greys the Generate buttons for everyone, not just for
 *  whoever pressed one.
 *
 *  Keyed on `status`, not on the entry existing — a finished job stays in `jobs`
 *  so its outcome can be shown, and treating that as busy would leave the buttons
 *  disabled for good. */
export const selectBlogArticleBusy = (articleId: string | undefined) => (state: RootState): boolean =>
  isBlogArticleBusy(state.blog.jobs, articleId);

/**
 * The same question asked of a `jobs` map already in hand.
 *
 * A table renders this per row, and a selector factory cannot be called from
 * inside a cell — so without this the rule gets copied, and it is exactly the
 * rule that is easy to get wrong: a job's PRESENCE is not the answer, because a
 * finished one stays in the map so its outcome can be shown.
 */
export const isBlogArticleBusy = (
  jobs: Record<string, BlogArticleJob>,
  articleId: string | undefined,
): boolean => Boolean(articleId) && jobs[articleId!]?.status === 'running';

/**
 * Why the last generation for this article failed, or null if none did.
 *
 * The service's own words, which is the only account of it that exists: a failed
 * pass writes nothing into the article, so without this the owner is left with a
 * draft that simply never changed. What the studio offers next — retry, or delete
 * the empty draft — hangs off this being non-null.
 */
export const selectBlogArticleJobError = (articleId: string | undefined) => (state: RootState): string | null => {
  const job = articleId ? state.blog.jobs[articleId] : undefined;

  return job?.status === 'failed' ? (job.error ?? GENERATION_FAILED) : null;
};

const selectBlogGeneratedIds = (state: RootState): string[] => state.blog.generated;

/**
 * The article list with this session's Lumio drafts pulled to the front (newest
 * first). Everything else keeps the order the service returned, so the only
 * articles that move are the ones the owner just watched being written.
 */
export const selectBlogArticlesLumioFirst = createSelector(
  [selectBlogArticles, selectBlogGeneratedIds],
  (articles, generated) => {
    if (generated.length === 0) return articles;
    const fresh = generated
      .map((id) => articles.find((article) => article.id === id))
      .filter((article): article is BlogArticle => article !== undefined);
    if (fresh.length === 0) return articles;
    const freshIds = new Set(fresh.map((article) => article.id));
    return [...fresh, ...articles.filter((article) => !freshIds.has(article.id))];
  },
);

export const selectBlogArticleById = (id: string) =>
  createSelector([selectBlogArticles], (articles) => articles.find((a) => a.id === id));

/** Articles of one category in reading order: pillar first, then sequence, then title. */
export const selectBlogCategoryReadingOrder = (categoryId: string) =>
  createSelector([selectBlogArticles], (articles) =>
    articles
      .filter((a) => a.categoryId === categoryId)
      .sort(
        (a, b) =>
          Number(b.pillar) - Number(a.pillar) || a.sequence - b.sequence || a.title.localeCompare(b.title),
      ),
  );

export default blogSlice.reducer;
